import React, { PropsWithChildren, RefObject, useReducer, useRef } from 'react'
import uniqueId from 'lodash.uniqueid'

// state shared with a react context
interface MenuState {
  buttonFocused?: boolean
  buttonRef?: RefObject<HTMLButtonElement>
  focusedIndex?: number
  menuId?: string
  open?: boolean
  selectedIndex?: number
}

// state mutations dispatched via a useReducer hook
type MenuAction =
  | { type: 'close'; restoreFocus?: boolean }
  | { type: 'closeOutside' }
  | { type: 'focusButton' }
  | { type: 'open'; selectFirstItem?: boolean }
  | { type: 'selectIndex'; index: number }

const menuReducer = (prevState: MenuState, action: any): MenuState => {
  switch (action.type) {
    case 'open':
      return {
        ...prevState,
        open: true,
        buttonFocused: false,
        selectedIndex: action.selectFirstItem ? 0 : -1,
        focusedIndex: 0,
      }
    case 'close':
      return {
        ...prevState,
        open: false,
        buttonFocused: action.restoreFocus,
        focusedIndex: -1,
        selectedIndex: -1,
      }
    case 'closeOutside':
      return {
        ...prevState,
        open: false,
        buttonFocused: false,
        focusedIndex: -1,
        selectedIndex: -1,
      }
    case 'focusButton':
      return {
        ...prevState,
        buttonFocused: true,
      }
    case 'selectIndex':
      return {
        ...prevState,
        focusedIndex: action.index,
        selectedIndex: action.index,
      }
    default:
      throw new Error()
  }
}

type MenuContextType = {
  state: MenuState
  dispatch: (action: MenuAction) => void
}

export const MenuContext = React.createContext<MenuContextType>({
  state: {},
  dispatch: () => {},
})

const Menu = ({ children }: PropsWithChildren<{}>) => {
  const initialState: MenuState = {
    buttonFocused: false,
    buttonRef: useRef<HTMLButtonElement>(null),
    menuId: uniqueId('menu-'),
    open: false,
    selectedIndex: -1,
  }
  const [state, dispatch] = useReducer(menuReducer, initialState)

  return <MenuContext.Provider value={{ state, dispatch }}>{children}</MenuContext.Provider>
}

export default Menu
